 /**
 * Модуль: страница «Аудит пользователей» в панели администратора.
 *
 * Задача модуля:
 * - отображать построчный журнал действий пользователей за выбранный период;
 * - дать возможность фильтровать, сортировать, постранично просматривать и экспортировать данные в Excel;
 * - единообразно вести себя с другими отчётами (например, «Статистика посещений» в audit-stats).
 *
 * Структура и паттерны:
 * - UserAuditPageConstants — константы и настройки страницы (период по умолчанию, брейкпоинты);
 * - UserAuditExcelExporter — класс-сервис экспорта в Excel (Strategy/Service: одна ответственность — экспорт);
 * - UserAuditPage — React-компонент страницы: только композиция UI, состояние и вызовы сервисов.
 *
 * Зависимости:
 * - useUserAuditStore — источник данных для грида (CustomStore + API);
 * - UserAuditGridConfig — конфигурация колонок и маппинг полей;
 * - LoadPanel — глобальный индикатор загрузки (управляется setLoading из стора).
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/auth';
import AccessDenied from '../../../components/access-denied/access-denied';
import LoadPanel from 'devextreme-react/load-panel';
import DataGrid, {
    Column,
    Pager,
    Paging,
    FilterRow,
    HeaderFilter,
    SearchPanel,
    Scrolling,
    Export,
    Selection,
    Sorting,
    RemoteOperations,
} from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { useUserAuditStore } from './useUserAuditStore';
import UserAuditGridConfig from './UserAuditGridConfig';
import '../audit.scss'
// ---------------------------------------------------------------------------
// Константы страницы
// ---------------------------------------------------------------------------

/** Ширина экрана (px), ниже которой включается скрытие колонок через иконку в заголовке грида. */
const BREAKPOINT_COLUMN_HIDE_PX = 1024;

const MS_IN_DAY = 24 * 60 * 60 * 1000;
/** Количество дней «назад» от текущей даты для начального периода начала. */
const DEFAULT_DAYS = 3;

/**
 * Константы и настройки страницы «Аудит пользователей».
 * Централизует «магические числа» и тексты, чтобы не размазывать их по компоненту.
 */
const UserAuditPageConstants = {
    breakpointColumnHidePx: BREAKPOINT_COLUMN_HIDE_PX,
    msInDay: MS_IN_DAY,
    defaultDays: DEFAULT_DAYS,

    /** Локаль и опции для форматирования даты периода в подписи и имени файла. */
    dateFormatOptions: { year: 'numeric', month: 'short', day: 'numeric' },
    dateLocale: 'ru-ru',

    /** Шаблоны имён листа и файла при экспорте. */
    exportSheetNameAll: (periodLabel) => `Аудит пользователей ${periodLabel}`,
    exportSheetNameSelected: (periodLabel) => `Аудит пользователей (выбранные) ${periodLabel}`,
};

// ---------------------------------------------------------------------------
// Сервис экспорта в Excel (паттерн Service / единая ответственность)
// ---------------------------------------------------------------------------

/**
 * Класс, отвечающий за экспорт грида «Аудит пользователей» в Excel.
 * Инкапсулирует работу с exceljs, exportDataGrid и file-saver.
 * Страница только передаёт событие onExporting и период — вся логика здесь.
 */
class UserAuditExcelExporter {
    /**
     * Форматирует период для подписей (например: "5 мар. 2025 г. - 8 мар. 2025 г.").
     *
     * @param {Date} periodStart
     * @param {Date} periodEnd
     * @returns {string}
     */
    static formatPeriodLabel(periodStart, periodEnd) {
        const opts = UserAuditPageConstants.dateFormatOptions;
        const locale = UserAuditPageConstants.dateLocale;
        return `${periodStart.toLocaleDateString(locale, opts)} - ${periodEnd.toLocaleDateString(locale, opts)}`;
    }

    /**
     * Выполняет экспорт: отменяет стандартное поведение DevExtreme, формирует книгу и сохраняет файл.
     *
     * @param {Object} event - объект события onExporting DataGrid (e).
     * @param {Date} periodStart - начало периода (для имени листа/файла).
     * @param {Date} periodEnd - конец периода.
     */
    static exportToFile(event, periodStart, periodEnd) {
        event.cancel = true;

        const component = event.component;
        const selectedRowKeys = component.getSelectedRowKeys();
        const dataSource = component.getDataSource();
        const totalCount = dataSource.totalCount() || 0;

        // Экспортируем только выбранные строки, если выбрана часть записей.
        const isSelectedOnly =
            selectedRowKeys?.length > 0 && selectedRowKeys.length < totalCount;

        const periodLabel = this.formatPeriodLabel(periodStart, periodEnd);
        const sheetName = isSelectedOnly
            ? UserAuditPageConstants.exportSheetNameSelected(periodLabel)
            : UserAuditPageConstants.exportSheetNameAll(periodLabel);
        const fileName = `${sheetName}.xlsx`;

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        exportDataGrid({
            component,
            worksheet,
            autoFilterEnabled: true,
            topLeftCell: { row: 1, column: 1 },
            selectedRowsOnly: isSelectedOnly,
        })
            .then(() => workbook.xlsx.writeBuffer())
            .then((buffer) =>
                saveAs(
                    new Blob([buffer], { type: 'application/octet-stream' }),
                    fileName
                )
            )
            .catch((err) => console.error('User audit export error:', err));
    }
}

// ---------------------------------------------------------------------------
// Начальные значения периода (фабрика для useState)
// ---------------------------------------------------------------------------

/**
 * Возвращает объект с датами по умолчанию для периода аудита:
 * начало — N дней назад, конец — «сейчас» (плюс запас в 1 день для границы суток).
 *
 * @returns {{ start: Date, end: Date }}
 */
function getDefaultPeriod() {
    const now = new Date();
    const msInDay = UserAuditPageConstants.msInDay;
    const defaultDays = UserAuditPageConstants.defaultDays;
    return {
        start: new Date(now.getTime() - msInDay * defaultDays),
        end: new Date(now.getTime() + msInDay),
    };
}

// ---------------------------------------------------------------------------
// Компонент страницы
// ---------------------------------------------------------------------------

/**
 * Страница «Аудит пользователей».
 * Построчный журнал: дата записи, ФИО, email, договор, эндпоинт, успешность.
 */
const UserAuditPage = () => {
    const { selectedDog } = useAuth();

    // Показывать ли контент или AccessDenied (пересчёт при смене договора или периода).
    const [visiblePage, setVisiblePage] = useState(true);
    // Глобальный индикатор загрузки: им управляет useUserAuditStore при запросах к API.
    const [loading, setLoading] = useState(false);
    // На узких экранах включаем возможность скрывать колонки через заголовок грида.
    /*const [columnHidingEnabled, setColumnHidingEnabled] = useState(
        window.innerWidth < UserAuditPageConstants.breakpointColumnHidePx
    );*/

    // Ленивая инициализация: даты по умолчанию вычисляются один раз при монтировании.
    const [periodStart, setPeriodStart] = useState(() => getDefaultPeriod().start);
    const [periodEnd, setPeriodEnd] = useState(() => getDefaultPeriod().end);

    const dataGridRef = useRef(null);

    // Стор передаётся в DataGrid как dataSource; при смене периода создаётся новый стор (useMemo в хуке).
    const auditStore = useUserAuditStore(periodStart, periodEnd, setLoading);

    // При смене договора или периода — принудительно показываем страницу (сброс состояния доступа).
    useEffect(() => {
        setVisiblePage(true);
    }, [selectedDog, periodStart, periodEnd]);

    // Адаптивность: на узких экранах включаем columnHidingEnabled.
    /*useEffect(() => {
        const onResize = () =>
            setColumnHidingEnabled(
                window.innerWidth < UserAuditPageConstants.breakpointColumnHidePx
            );
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);*/

    const onStartPickerChanged = useCallback((e) => {
        if (e?.value) setPeriodStart(e.value);
    }, []);
    const onEndPickerChanged = useCallback((e) => {
        if (e?.value) setPeriodEnd(e.value);
    }, []);

    const onExporting = useCallback(
        (e) => {
            UserAuditExcelExporter.exportToFile(e, periodStart, periodEnd);
        },
        [periodStart, periodEnd]
    );

    const columns = UserAuditGridConfig.getColumns();

    if (!visiblePage) {
        return <AccessDenied />;
    }

    return (
        <div className="content-block dx-card responsive-paddings">
            <FontAwesomeIcon
                className="icon-header-h3 main-color main-color-light"
                icon={faUserCheck}
            />
            <h3><strong>Аудит пользователей</strong></h3>

            <div className="row big-input">
                <div className="col-sm-4 col-md-3 col-lg-2" style={{ minWidth: '200px' }}>
                    <DateBox
                        labelMode="floating"
                        label="Период начала:"
                        useMaskBehavior
                        value={periodStart}
                        onValueChanged={onStartPickerChanged}
                    />
                </div>
                <div className="col-sm-4 col-md-3 col-lg-2 mt20xs" style={{ minWidth: '200px' }}>
                    <DateBox
                        labelMode="floating"
                        label="Период окончания:"
                        useMaskBehavior
                        value={periodEnd}
                        onValueChanged={onEndPickerChanged}
                    />
                </div>
            </div>

            <div style={{ textAlign: 'justify' }}>
                <DataGrid
                    ref={dataGridRef}
                    key={`user-audit-${periodStart.getTime()}-${periodEnd.getTime()}`}
                    height={660}
                    style={{ margin: '20px 0' }}
                    keyExpr="id"
                    // columnHidingEnabled={columnHidingEnabled}
                    allowColumnReordering
                    rowAlternationEnabled
                    showBorders
                    columnAutoWidth={false}
                    noDataText="За выбранный период записи отсутствуют"
                    dataSource={auditStore}
                    onExporting={onExporting}
                    loadPanel={{ enabled: false }}
                    className={'export-btn-no-wrap'}
                    allowColumnResizing={true}
                    columnWidth={'auto'}
                    width={'100%'}
                >
                    <RemoteOperations paging filtering sorting grouping={false} />
                    <Export
                        enabled
                        allowExportSelectedData
                        texts={{
                            exportAll: 'Экспортировать все строки в Excel',
                            exportSelected: 'Экспортировать выбранные строки в Excel',
                        }}
                    />
                    <Selection mode="multiple" />
                    <Scrolling mode="standard" />
                    <Sorting mode="single" />
                    <FilterRow visible />
                    <HeaderFilter visible />
                    <SearchPanel visible width={240} placeholder="Поиск..." />
                    <Paging defaultPageSize={50} enabled />
                    <Pager
                        visible
                        allowedPageSizes={[5, 10, 20, 50, 100]}
                        displayMode="full"
                        showPageSizeSelector
                        showInfo
                        showNavigationButtons
                        infoText="Страница {0} из {1} ({2} записей)"
                    />
                    <Column type="selection" width="3%" minWidth={50} />
                    {columns.map((col) => (
                        <Column
                            key={col.dataField}
                            dataField={col.dataField}
                            caption={col.caption}
                            dataType={col.dataType}
                            allowEditing={false}
                            width={col.width}
                            minWidth={col.minWidth}
                            sortOrder={col.sortOrder}
                            defaultSortIndex={col.defaultSortIndex}
                            customizeText={
                                col.dataField === 'success'
                                    ? (cellInfo) => (cellInfo.value ? 'Да' : 'Нет')
                                    : undefined
                            }
                        />
                    ))}
                </DataGrid>
            </div>

            {/* Индикатор загрузки при запросах к API; состояние задаётся в useUserAuditStore. */}
            <LoadPanel visible={loading} showIndicator />
        </div>
    );
};

export default UserAuditPage;
